// List the signed-in seller's submitted offers, enriched with the parent
// buy order and (when one exists) the resulting transaction. RLS lets a
// seller read their own offers + the buy_orders/transactions they're a
// party to, so we use the request-scoped supabase client.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type MySellerOffer = {
  id: string;
  status: string;
  price_cents: number;
  currency: string;
  created_at: string;
  is_buy_it_now: boolean;
  buy_order: {
    id: string;
    title: string | null;
    card_name: string | null;
    tcg: string | null;
    status: string;
    max_price_cents: number;
    currency: string;
    locked_by_offer_id: string | null;
  } | null;
  transaction: {
    id: string;
    status: string;
    shipped_at: string | null;
    delivered_at: string | null;
    released_at: string | null;
    refunded_at: string | null;
  } | null;
};


export const listMySellerOffers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ offers: MySellerOffer[] }> => {
    const { supabase, userId } = context;

    const { data: offers, error } = await supabase
      .from("offers")
      .select(
        "id, status, price_cents, currency, created_at, is_buy_it_now, buy_order_id, buy_orders(id, title, card_name, tcg, status, max_price_cents, currency, locked_by_offer_id)",
      )
      .eq("seller_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);

    const buyOrderIds = Array.from(
      new Set((offers ?? []).map((o) => o.buy_order_id).filter((v): v is string => !!v)),
    );

    const txnByOffer = new Map<string, MySellerOffer["transaction"]>();
    if (buyOrderIds.length > 0) {
      const { data: txns } = await supabase
        .from("transactions")
        .select(
          "id, offer_id, status, shipped_at, delivered_at, released_at, refunded_at",
        )
        .in("buy_order_id", buyOrderIds)
        .eq("seller_id", userId);
      for (const t of txns ?? []) {
        if (t.offer_id) {
          txnByOffer.set(t.offer_id, {
            id: t.id,
            status: t.status,
            shipped_at: t.shipped_at,
            delivered_at: t.delivered_at,
            released_at: t.released_at,
            refunded_at: t.refunded_at,
          });
        }
      }
    }


    const result: MySellerOffer[] = (offers ?? []).map((o) => {
      const bo = (o as unknown as { buy_orders: MySellerOffer["buy_order"] }).buy_orders ?? null;
      return {
        id: o.id,
        status: o.status,
        price_cents: o.price_cents,
        currency: o.currency,
        created_at: o.created_at,
        is_buy_it_now: !!o.is_buy_it_now,
        buy_order: bo,
        transaction: txnByOffer.get(o.id) ?? null,
      };
    });

    return { offers: result };
  });
